import { useEffect, useState } from "react";
import { ChevronDown, Coins, Info } from "lucide-react";
import { useApi } from "@/lib/api";

const money = (v, cur) => {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: v % 1 ? 2 : 0 }).format(v); }
  catch { return `${cur} ${v.toLocaleString()}`; }
};

const Bars = ({ title, rows, cur, testid }) => {
  const max = Math.max(...rows.map((r) => r.total), 1);
  return (
    <div data-testid={testid}>
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-ww-emerald">{title}</p>
      <div className="space-y-3">
        {rows.slice(0, 8).map((r, i) => (
          <div key={r.label} data-testid={`${testid}-row-${i}`}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate font-semibold text-ww-navy">{r.label} <span className="font-normal text-ww-muted">· {r.count}</span></span>
              <span className="shrink-0 font-semibold text-ww-navy">{money(r.total, cur)}</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-ww-cyan">
              <div className="ww-reveal h-2 rounded-full bg-[linear-gradient(90deg,#087C65,#08A881)]" style={{ width: `${Math.max(4, (r.total / max) * 100)}%`, animationDelay: `${i * 0.05}s` }} />
            </div>
          </div>
        ))}
        {rows.length > 8 && <p className="text-xs text-ww-muted">+ {rows.length - 8} more</p>}
      </div>
    </div>
  );
};

function Protection({ c }) {
  const pct = c.total ? Math.round((c.protected / c.total) * 100) : 0;
  const r = 34;
  const len = 2 * Math.PI * r;
  const rows = [
    ["Still covered", c.protected, c.protected_count, "bg-ww-bright", "protected"],
    ["Expiring within 30 days", c.expiring, c.expiring_count, "bg-amber-500", "expiring"],
    ["No active coverage", c.unprotected, c.unprotected_count, "bg-[#CBD5E1]", "unprotected"],
  ];
  return (
    <div className="mb-8 flex flex-col gap-6 rounded-2xl bg-ww-mint/50 p-5 sm:flex-row sm:items-center" data-testid="protection-overview">
      <div className="relative h-24 w-24 shrink-0">
        <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#E5EAF0" strokeWidth="8" />
          <circle cx="40" cy="40" r={r} fill="none" stroke="#08A881" strokeWidth="8" strokeLinecap="round" strokeDasharray={len} strokeDashoffset={len * (1 - pct / 100)} className="transition-[stroke-dashoffset] duration-1000" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-display text-xl font-semibold text-ww-navy" data-testid="protection-percent">{pct}%</span><span className="text-[10px] font-semibold uppercase text-ww-muted">covered</span></div>
      </div>
      <div className="flex-1 space-y-2.5">
        <p className="text-sm font-semibold text-ww-navy">Protection overview</p>
        {rows.map(([l, v, n, dot, k]) => (
          <div key={k} className="flex items-center justify-between gap-3 text-sm" data-testid={`protection-${k}`}>
            <span className="flex items-center gap-2 text-ww-navy/80"><span className={`h-2.5 w-2.5 rounded-full ${dot}`} />{l} <span className="text-ww-muted">· {n}</span></span>
            <span className="font-semibold text-ww-navy">{money(v, c.currency)}</span>
          </div>
        ))}
        <p className="text-[11px] text-ww-muted">"Expiring within 30 days" is part of the covered amount.</p>
      </div>
    </div>
  );
}

export function SpendingSummary({ refreshKey }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [cur, setCur] = useState(null);
  useEffect(() => { api("get", "/wallet/summary").then((d) => { setData(d); setCur((c) => c || d.currencies[0]?.currency); }).catch(() => {}); }, [api, refreshKey]);
  if (!data || data.total_products === 0) return null;
  const c = data.currencies.find((x) => x.currency === cur) || data.currencies[0];
  return (
    <section className="mt-6 rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="spending-summary">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-4 text-left" data-testid="spending-summary-toggle">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><Coins className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-ww-muted">Total value stored in your wallet</p>
            <p className="font-display text-3xl font-semibold text-ww-navy" data-testid="spending-total">{c ? money(c.total, c.currency) : "—"}</p>
          </div>
        </div>
        <ChevronDown className={`h-5 w-5 text-ww-muted transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-6">
          {data.currencies.length > 1 && (
            <div className="mb-6 flex flex-wrap gap-2" data-testid="spending-currency-tabs">
              {data.currencies.map((x) => (
                <button key={x.currency} onClick={() => setCur(x.currency)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${x.currency === c.currency ? "bg-ww-navy text-white" : "bg-ww-cyan text-ww-navy"}`} data-testid={`spending-currency-${x.currency}`}>{x.currency} · {x.count}</button>
              ))}
            </div>
          )}
          {c && <Protection c={c} />}
          {c ? (
            <div className="grid gap-8 md:grid-cols-2">
              <Bars title="By brand" rows={c.brands} cur={c.currency} testid="spending-by-brand" />
              <Bars title="By purchase year" rows={c.years} cur={c.currency} testid="spending-by-year" />
            </div>
          ) : <p className="text-sm text-ww-muted">Add a purchase price to your products to see their total value here.</p>}
          <p className="mt-6 flex items-start gap-2 text-xs text-ww-muted" data-testid="spending-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Based on {data.priced} product{data.priced === 1 ? "" : "s"} with a purchase price{data.unpriced ? `; ${data.unpriced} without a price ${data.unpriced === 1 ? "is" : "are"} not included` : ""}.
            {data.currencies.length > 1 ? " Totals are shown per currency and never converted." : ""}
          </p>
        </div>
      )}
    </section>
  );
}
